/**
 * Секция «Избранное» в левом рельсе.
 *
 * Показывает закреплённые пользователем пространства и представления (доски)
 * для быстрого доступа. Порядок задаёт сам пользователь перетаскиванием
 * (@dnd-kit sortable). Источник данных — персональные преференсы
 * (`ui:favorites`), см. shared/prefs/store.ts.
 */
import { Tooltip } from 'antd';
import { HolderOutlined, ProjectOutlined, StarFilled, TableOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import { SpaceAvatar } from '../components/SpaceAvatar.tsx';
import { getSpaceIconDef, resolveSpaceColor, resolveSpaceIconKey } from '../components/spaceIcons.ts';
import {
  type FavoriteEntry,
  favoriteKey,
  removeFavorite,
  setFavoritesOrder,
} from '../prefs/store.ts';
import styles from './AppLayout.module.css';

function favLink(entry: FavoriteEntry): string {
  return entry.kind === 'space'
    ? `/s/${entry.slug}`
    : `/s/${entry.spaceSlug}/views/${entry.id}`;
}

/** Иконка записи избранного: аватар для пространства, глиф для представления. */
function favIcon(entry: FavoriteEntry): React.ReactNode {
  if (entry.kind === 'space') {
    return <SpaceAvatar name={entry.name} icon={entry.icon} seed={entry.id} size={18} />;
  }
  const def = getSpaceIconDef(resolveSpaceIconKey(entry.icon) ?? '');
  if (def) {
    return (
      <def.Icon
        size={14}
        strokeWidth={1.9}
        color={resolveSpaceColor(entry.icon) ?? 'currentColor'}
        style={{ verticalAlign: '-2px' }}
      />
    );
  }
  return entry.viewType === 'kanban' ? <ProjectOutlined /> : <TableOutlined />;
}

interface FavoriteRowProps {
  entry: FavoriteEntry;
  active: boolean;
  /** Имя пространства представления (для tooltip'а и бейджа-дизамбигуатора). */
  spaceName?: string;
  /** Показать бейдж пространства — в избранном есть одноимённое представление. */
  showSpace: boolean;
}

function FavoriteRow({ entry, active, spaceName, showSpace }: FavoriteRowProps): JSX.Element {
  const id = favoriteKey(entry.kind, entry.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.favRow} ${active ? styles.favRowActive : ''}`}
    >
      <button
        type="button"
        className={styles.favHandle}
        aria-label="Переместить"
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
      <Link
        to={favLink(entry)}
        className={styles.favLink}
        title={spaceName ? `${spaceName} · ${entry.name}` : entry.name}
      >
        <span className={styles.favIcon}>{favIcon(entry)}</span>
        <span className={styles.favName}>{entry.name}</span>
        {showSpace && spaceName ? <span className={styles.favSuffix}>{spaceName}</span> : null}
      </Link>
      <Tooltip title="Убрать из избранного" placement="right">
        <button
          type="button"
          className={styles.favStar}
          aria-label="Убрать из избранного"
          onClick={() => removeFavorite(entry.kind, entry.id)}
        >
          <StarFilled />
        </button>
      </Tooltip>
    </div>
  );
}

interface FavoritesSectionProps {
  favorites: FavoriteEntry[];
  activeSpaceId: string | null;
  activeViewId: string | null;
  /**
   * id пространства → актуальное имя (из загруженного списка пространств).
   * В FavoriteEntry имени пространства нет — резолвим на месте, чтобы подпись
   * не протухала при переименовании; fallback — spaceSlug из записи.
   */
  spaceNameById: ReadonlyMap<string, string>;
}

export function FavoritesSection({
  favorites,
  activeSpaceId,
  activeViewId,
  spaceNameById,
}: FavoritesSectionProps): JSX.Element | null {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  if (favorites.length === 0) return null;

  const ids = favorites.map((f) => favoriteKey(f.kind, f.id));

  // Одноимённые представления из разных пространств неразличимы по подписи —
  // у таких показываем бейдж пространства. Уникальные имена не захламляем.
  const viewNameCounts = new Map<string, number>();
  for (const f of favorites) {
    if (f.kind !== 'view') continue;
    const k = f.name.trim().toLowerCase();
    viewNameCounts.set(k, (viewNameCounts.get(k) ?? 0) + 1);
  }

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    setFavoritesOrder(arrayMove(favorites, oldIndex, newIndex));
  };

  const isActive = (entry: FavoriteEntry): boolean =>
    entry.kind === 'space'
      ? activeSpaceId === entry.id && !activeViewId
      : activeViewId === entry.id;

  return (
    <div className={styles.favSection} data-testid="rail-favorites">
      <div className={styles.sectionLabel}>Избранное</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className={styles.favList}>
            {favorites.map((entry) => (
              <FavoriteRow
                key={favoriteKey(entry.kind, entry.id)}
                entry={entry}
                active={isActive(entry)}
                spaceName={
                  entry.kind === 'view'
                    ? spaceNameById.get(entry.spaceId) ?? entry.spaceSlug
                    : undefined
                }
                showSpace={
                  entry.kind === 'view' &&
                  (viewNameCounts.get(entry.name.trim().toLowerCase()) ?? 0) > 1
                }
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
